"use client";

import { useState } from "react";
import { authClient } from "@/lib/auth-client";
import Link from "next/link";
import { Beef } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);

    await authClient.requestPasswordReset({
      email,
      redirectTo: "/reset-password",
    });

    // Always show success — prevents user enumeration
    setSent(true);
    setLoading(false);
  }

  if (sent) {
    return (
      <div className="min-h-[calc(100vh-4rem)] bg-[var(--color-background)] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm text-center">
          <Beef className="w-10 h-10 text-[var(--color-accent)] mx-auto mb-3" strokeWidth={1.5} />
          <h1 className="text-xl font-heading font-bold mb-2">Check Your Email</h1>
          <p className="text-sm text-[var(--color-muted)] mb-6">
            If an account exists for {email || "that email"}, we&apos;ve sent a password reset link.
            The link expires in 1 hour.
          </p>
          <Link
            href="/sign-in"
            className="inline-block text-sm text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[var(--color-background)] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <Beef className="w-10 h-10 text-[var(--color-accent)] mx-auto mb-3" strokeWidth={1.5} />
          <h1 className="text-xl font-heading font-bold text-[var(--color-foreground)]">Forgot Password</h1>
          <p className="text-sm text-[var(--color-muted)] mt-1">We&apos;ll send you a reset link</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-[var(--color-border)] rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-[var(--color-border)] rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/30 focus:border-[var(--color-accent)]"
              placeholder="you@example.com"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full px-4 py-2.5 bg-[var(--color-accent)] text-white rounded-lg font-medium hover:bg-[var(--color-accent-light)] transition-colors disabled:opacity-50"
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>

          <div className="text-center text-sm">
            <Link
              href="/sign-in"
              className="text-[var(--color-muted)] hover:text-[var(--color-accent)] transition-colors"
            >
              Back to sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
